import { ObjectValuesUnionType } from '../utils/typing';

export const MatchBetFiltersPatternsPrefixes = Object.freeze({
  BET_CATEGORY: 'betCategory',
  PLAYER_ID: 'playerId',
  MARKET_ID: 'marketId',
  SBV: 'specialBetValue',
  IS_FROM_MULTICHOICE: 'isFromMultichoice',
  BET_TYPE: 'betType',
});
export type MatchBetFiltersPattern = ObjectValuesUnionType<typeof MatchBetFiltersPatternsPrefixes>;

export const MatchBetFiltersPatternParameterSeparator = '%%%%%';

export const MatchBetFiltersPatterns = {
  [MatchBetFiltersPatternsPrefixes.BET_CATEGORY]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.BET_CATEGORY}=([0-9]+)`,
  ),
  [MatchBetFiltersPatternsPrefixes.PLAYER_ID]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.PLAYER_ID}=([0-9]+)`,
  ),
  [MatchBetFiltersPatternsPrefixes.MARKET_ID]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.MARKET_ID}=([0-9]+)`,
  ),
  [MatchBetFiltersPatternsPrefixes.BET_TYPE]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.BET_TYPE}=([0-9]+)`,
  ),
  [MatchBetFiltersPatternsPrefixes.SBV]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.SBV}=([^${MatchBetFiltersPatternParameterSeparator}]+)`,
  ),
  [MatchBetFiltersPatternsPrefixes.IS_FROM_MULTICHOICE]: new RegExp(
    `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.IS_FROM_MULTICHOICE}=(true|false)`,
  ),
};

export const getMatchBetFilterPattern = (prefix: MatchBetFiltersPattern, value: number) =>
  `${MatchBetFiltersPatternParameterSeparator}${prefix}=${value}`;

export const getIdFromFilterIdWithPattern = (filterId: string, pattern: RegExp): string | number | null => {
  if (pattern) {
    const idFromFilter = filterId.match(pattern)?.[1] ?? null;

    if (idFromFilter) {
      const parsedAsNumber = parseInt(idFromFilter, 10);

      if (!Number.isNaN(parsedAsNumber)) {
        return parsedAsNumber;
      }

      return idFromFilter;
    }

    return null;
  }

  return null;
};

export const getBetCategoryIdFromFilterId = (filterId: string): number | null => {
  if (filterId) {
    const idFromFilterId = getIdFromFilterIdWithPattern(
      filterId,
      MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.BET_CATEGORY],
    );

    if (typeof idFromFilterId === 'number') {
      return idFromFilterId;
    }

    return null;
  }

  return null;
};

export const getPlayerIdFromFilterId = (filterId: string): number | null => {
  if (filterId) {
    const idFromFilterId = getIdFromFilterIdWithPattern(
      filterId,
      MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.PLAYER_ID],
    );

    if (typeof idFromFilterId === 'number') {
      return idFromFilterId;
    }

    return null;
  }

  return null;
};

export const getMarketIdFromFilterId = (filterId?: string): number | null => {
  if (filterId) {
    const idFromFilterId = getIdFromFilterIdWithPattern(
      filterId,
      MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.MARKET_ID],
    );

    if (typeof idFromFilterId === 'number') {
      return idFromFilterId;
    }

    return null;
  }

  return null;
};

export const getBetTypeFromFilterId = (filterId?: string): number | null => {
  if (filterId) {
    const idFromFilterId = getIdFromFilterIdWithPattern(
      filterId,
      MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.BET_TYPE],
    );

    if (typeof idFromFilterId === 'number') {
      return idFromFilterId;
    }

    return null;
  }

  return null;
};

export const getSBVFromFilterId = (filterId?: string): number | string | null => {
  if (filterId) {
    return getIdFromFilterIdWithPattern(filterId, MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.SBV]);
  }

  return null;
};

export const getIsFromMultichoiceFromFilterId = (filterId: string | null): number | null => {
  if (filterId) {
    const raw = getIdFromFilterIdWithPattern(
      filterId,
      MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.IS_FROM_MULTICHOICE],
    );

    try {
      return JSON.parse(`${raw}`);
    } catch (e) {
      console.error(e);
    }
  }

  return null;
};

export const getFilterIdFromParams = ({
  marketId,
  sbv,
  isFromMultichoice,
  playerId,
  betCategoryId,
  betType,
}: {
  marketId?: number;
  sbv?: string;
  isFromMultichoice?: boolean;
  playerId?: number;
  betCategoryId?: number;
  betType?: number;
}) => {
  let res = '';

  if (marketId !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.MARKET_ID}=${marketId}`;
  }

  if (betType !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.BET_TYPE}=${betType}`;
  }

  if (sbv !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.SBV}=${sbv}`;
  }

  if (isFromMultichoice !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.IS_FROM_MULTICHOICE}=${
      isFromMultichoice ? 'true' : 'false'
    }`;
  }

  if (playerId !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.PLAYER_ID}=${playerId}`;
  }

  if (betCategoryId !== undefined) {
    res += `${MatchBetFiltersPatternParameterSeparator}${MatchBetFiltersPatternsPrefixes.BET_CATEGORY}=${betCategoryId}`;
  }

  return res || undefined;
};
